import type { ProjectMilestone, ProjectMilestoneStrip } from '@interfaces/IProject';

/** One milestone as the current/next boxes show it. */
export type IMilestoneHighlight = {
  name: string;
  dateIso: string;
  /** Calendar days from today to the milestone's day: negative once it has passed. */
  daysFromToday: number;
  progressPct: number;
};

/** What the card reads off a project's milestone strip. */
export type IMilestoneSummary = {
  completed: number;
  total: number;
  /** First and last milestone days — the strip's 0% and 100% ends. Null without milestones. */
  range: { startIso: string; endIso: string } | null;
  /** The earliest milestone not yet completed — the strip's `current`. */
  current: IMilestoneHighlight | null;
  currentDelayed: boolean;
  /** The milestone after `current` that is not yet completed. */
  next: IMilestoneHighlight | null;
};

const DAY_MS = 86_400_000;

/** Days between two calendar days (YYYY-MM-DD; any time part is ignored). */
function daysBetween(fromYmd: string, toYmd: string): number {
  const utc = (ymd: string) => Date.parse(`${ymd.slice(0, 10)}T00:00:00Z`);
  return Math.round((utc(toYmd) - utc(fromYmd)) / DAY_MS);
}

function highlight(m: ProjectMilestone, todayYmd: string): IMilestoneHighlight {
  return {
    name: m.name,
    dateIso: m.dateIso,
    daysFromToday: daysBetween(todayYmd, m.dateIso),
    progressPct: m.progressPercent,
  };
}

/**
 * Counts, ends, and the current/next milestones of a strip, judged against
 * `todayYmd`. "Current" follows the backend's rule (the earliest milestone not
 * done), so it is the same milestone the strip names; whether it is late comes
 * from the strip too, which judges it on the site's calendar.
 */
export function summarizeMilestones(strip: ProjectMilestoneStrip, todayYmd: string): IMilestoneSummary {
  const byDate = [...strip.milestones].sort((a, b) => a.dateIso.localeCompare(b.dateIso));
  const open = byDate.filter((m) => m.state !== 'done');
  const [current, next] = open;
  const [first] = byDate;
  return {
    completed: byDate.length - open.length,
    total: byDate.length,
    range: first ? { startIso: first.dateIso, endIso: byDate[byDate.length - 1]!.dateIso } : null,
    current: current ? highlight(current, todayYmd) : null,
    currentDelayed: strip.currentDelayed,
    next: next ? highlight(next, todayYmd) : null,
  };
}
